import { useCallback, useEffect, useReducer, useRef } from 'react';
import { OFFLINE_THRESHOLD_MS, nextRecheckDelay, probeConnectivity } from 'helpers/connectivity';

/**
 * Is the network reachable? — the state machine behind the no-network page.
 *
 * Mounted once per portal root (AdminApp / PartnerApp), so it covers the login
 * screen as well as the console. Only one portal renders per document, so
 * this is one probe loop per tab.
 *
 * ── The loop, in one paragraph ─────────────────────────────────────────────
 * EVENT-DRIVEN while online: one probe on mount, then nothing until the
 * browser's `online` / `offline` events or the tab becoming visible again —
 * those only TRIGGER a probe (the `offline` event is itself `navigator.onLine`
 * based, i.e. unreliable); the probe decides. Once a probe fails, a failure
 * streak starts and the hook re-probes on a recursive `setTimeout` (never
 * `setInterval`, so probes cannot overlap) every FAILING_RECHECK_MS until
 * either the streak has lasted OFFLINE_THRESHOLD_MS (the page shows) or a
 * probe succeeds (the page hides, the loop stops). The page hides on its own;
 * Retry just asks sooner.
 *
 * ── The streak is stamped at the START of the failed probe ─────────────────
 * A black-holed network does not reject, it hangs, so the first probe only
 * fails when its 11 s timeout fires. Stamping at the END would make the page
 * wait a further 11 s of rechecks — 22 s to name a dead network. Counting the
 * probe's own duration shows it at 11 s, which is what the threshold means.
 *
 * ── One request at a time, and no answer older than the question ───────────
 * `inFlightRef` holds the current probe. A scheduled tick, an event or a Retry
 * click that lands while one is in flight JOINS it rather than starting a
 * second request — but marks it `superseded`, because something changed after
 * that request began (the cable may just have gone back in). A superseded
 * probe's FAILURE is not shown (no page flash, no "still offline" hint on a
 * click that deserved a fresh answer); the streak stamp is kept and one
 * follow-up probe runs immediately. Its SUCCESS is applied at once.
 *
 * ── Why a reducer ──────────────────────────────────────────────────────────
 * `offline`, `checking` and `retryFailed` change together (one good probe
 * clears all three), and the transition table is worth testing without React.
 *
 * ── What this deliberately does not do ─────────────────────────────────────
 * It never touches the session layer: an outage is not a sign-out
 * (`portalClient` already returns GENERIC_FAILURE / REFRESH_UNREACHABLE for
 * transport errors), and each portal's single `onSessionExpired` slot is
 * already held by its auth layer. It never reads `navigator.onLine`.
 *
 * @param {{ probe?: () => Promise<boolean> }} [options] `probe` is injectable for tests; production passes nothing.
 * @returns {{ offline: boolean, checking: boolean, retryFailed: boolean, retry: () => Promise<boolean> }}
 */

export const INITIAL_CONNECTIVITY = { offline: false, checking: false, retryFailed: false };

export function connectivityReducer(state, action) {
  switch (action.type) {
    case 'reachable':
      // One good probe clears the page, the busy state and the hint together.
      return INITIAL_CONNECTIVITY;
    case 'unreachable':
      return {
        // Monotonic: only 'reachable' takes the page down.
        offline: state.offline || Boolean(action.pastThreshold),
        checking: false,
        // A failed Retry raises the hint; a scheduled failure never clears it.
        retryFailed: Boolean(action.viaRetry) || state.retryFailed,
      };
    case 'retrying':
      return { ...state, checking: true, retryFailed: false };
    default:
      return state;
  }
}

export default function useConnectivity({ probe = probeConnectivity } = {}) {
  const [state, dispatch] = useReducer(connectivityReducer, INITIAL_CONNECTIVITY);

  // Assigned during render, so the effect below keeps stable deps and a test
  // can swap the mock between ticks.
  const probeRef = useRef(probe);
  probeRef.current = probe;

  /**
   * Mount generation. Bumped on cleanup, captured by every probe: a
   * continuation from a previous mount (unmount mid-probe, or a StrictMode
   * remount if that is ever switched on) compares and bails instead of
   * dispatching into a tree it no longer belongs to.
   */
  const generationRef = useRef(0);
  const timerRef = useRef(null);
  const inFlightRef = useRef(null);
  const firstFailureAtRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const settle = useCallback(
    (viaRetry) => {
      const current = inFlightRef.current;
      if (current) {
        current.viaRetry = current.viaRetry || viaRetry;
        current.superseded = true;
        return current.promise;
      }

      // A fresh probe replaces whatever re-check was pending.
      clearTimer();
      const generation = generationRef.current;
      const startedAt = Date.now();
      const entry = { viaRetry, superseded: false, promise: null };

      const schedule = (ms, nextViaRetry) => {
        clearTimer();
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          settle(nextViaRetry);
        }, ms);
      };

      // Called synchronously so a click or an event is a request straight away.
      let raw;
      try {
        raw = probeRef.current();
      } catch {
        raw = false;
      }

      entry.promise = Promise.resolve(raw)
        .then(
          (online) => Boolean(online),
          () => false,
        )
        .then((online) => {
          if (inFlightRef.current === entry) inFlightRef.current = null;
          if (generation !== generationRef.current) return online;

          if (online) {
            firstFailureAtRef.current = null;
            clearTimer();
            dispatch({ type: 'reachable' });
            // Something changed while this was in flight: give it a fresh answer, then stop.
            if (entry.superseded) schedule(0, false);
            return true;
          }

          if (firstFailureAtRef.current === null) firstFailureAtRef.current = startedAt;
          const elapsed = Date.now() - firstFailureAtRef.current;

          if (entry.superseded) {
            // Stale: never show the page, or the hint, on a result that predates the question.
            schedule(0, entry.viaRetry);
            return false;
          }

          dispatch({
            type: 'unreachable',
            pastThreshold: elapsed >= OFFLINE_THRESHOLD_MS,
            viaRetry: entry.viaRetry,
          });
          schedule(nextRecheckDelay(elapsed), false);
          return false;
        });

      inFlightRef.current = entry;
      return entry.promise;
    },
    [clearTimer],
  );

  useEffect(() => {
    settle(false); // establish state on mount

    const rerun = () => {
      clearTimer();
      settle(false);
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') rerun();
    };
    window.addEventListener('online', rerun);
    window.addEventListener('offline', rerun);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      generationRef.current += 1;
      clearTimer();
      inFlightRef.current = null;
      firstFailureAtRef.current = null;
      window.removeEventListener('online', rerun);
      window.removeEventListener('offline', rerun);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [settle, clearTimer]);

  const retry = useCallback(() => {
    dispatch({ type: 'retrying' });
    return settle(true);
  }, [settle]);

  return {
    offline: state.offline,
    checking: state.checking,
    retryFailed: state.retryFailed,
    retry,
  };
}
